import { type FC, useMemo } from "react";

import { useHistory, useLocation } from "react-router-dom";

import { Page } from "@alpacahq/ui/old";

import {
  useGetCurrentAccountIfLiveElseTradingAccount,
  useGetOwnerDetails,
  useGetTrustedContact,
  useIsCryptoSupported,
} from "~/v2/api/hooks";
import type {
  Account,
  OwnerDetails,
  TrustedContact,
} from "~/v2/api/rest/account";
import { FullScreenSpinner } from "~/v2/components/common";
import { isPaperOrOnboarding } from "~/v2/utils";
import App from "../../index";
import SettingsNavigation from "./components/settingsNavigation";
import {
  PROFILE_TAB_DEFINITIONS,
  type ProfileTabSlug,
  ProfileTabs,
  parseHash,
  profileTabPath,
} from "./profileTabs";
import Additional from "./tabs/additional";
import { Agreements } from "./tabs/agreements/Agreements.tab";
import Basic from "./tabs/basic";
import ConnectedApps from "./tabs/connectedApps";
import Crypto from "./tabs/crypto";
import DocumentRequests from "./tabs/documentRequests";
import { IPAllowlist } from "./tabs/ipAllowlist";
import ManageAccounts from "./tabs/manageAccounts";
import Security from "./tabs/security";
import Trusted from "./tabs/trusted";

export interface SettingsTabProps {
  account: Account;
  details: OwnerDetails;
  trusted_contact?: TrustedContact | null;
  hasOnboarded: boolean;
}

type SettingsTabComponent = {
  name: string;
  slug: ProfileTabSlug;
  component: FC<SettingsTabProps>;
};

const TAB_COMPONENTS: Record<ProfileTabSlug, FC<SettingsTabProps>> = {
  [ProfileTabs.BASIC_INFO]: Basic,
  [ProfileTabs.MANAGE_ACCOUNTS]: ManageAccounts,
  [ProfileTabs.DOCUMENT_REQUESTS]: DocumentRequests,
  [ProfileTabs.ADDITIONAL_INFO]: Additional,
  [ProfileTabs.TRUSTED_CONTACT]: Trusted,
  [ProfileTabs.AGREEMENTS]: Agreements,
  [ProfileTabs.SECURITY]: Security,
  [ProfileTabs.CONNECTED_APPS]: ConnectedApps,
  [ProfileTabs.CRYPTO_FEES]: Crypto,
  [ProfileTabs.IP_ALLOWLIST]: IPAllowlist,
};

const tabs: SettingsTabComponent[] = PROFILE_TAB_DEFINITIONS.map(
  ({ name, slug }) => ({
    name,
    slug,
    component: TAB_COMPONENTS[slug],
  }),
);

const isNotFullActiveAccount = (account: Account | undefined) =>
  !account ||
  isPaperOrOnboarding(account) ||
  account.status === "ACCOUNT_CLOSED" ||
  account.status === "DISABLED";

export const getCanTransferCrypto = (
  account?: Account,
  isCryptoSupported = false,
) =>
  isCryptoSupported &&
  !isNotFullActiveAccount(account) &&
  // whether the user has crypto enabled
  ["ACTIVE"].includes(account?.crypto_status || "");

const HIDDEN_WHEN_NOT_ONBOARDED: ProfileTabSlug[] = [
  ProfileTabs.TRUSTED_CONTACT,
  ProfileTabs.CRYPTO_FEES,
];

const ProfileSettings = () => {
  const history = useHistory();
  const location = useLocation();

  const { account, refetch: refetchAccount } =
    useGetCurrentAccountIfLiveElseTradingAccount();
  const { details, refetch: refetchDetails } = useGetOwnerDetails({
    options: {
      gcTime: 0,
    },
  });

  // todo: this returns as {} if no trusted contact, should be null in the future
  const { trusted_contact } = useGetTrustedContact({
    accountID: account?.id,
    options: {
      enabled: !!account?.id,
      gcTime: 0,
    },
  });

  const { isCryptoSupported } = useIsCryptoSupported();

  const canTransferCrypto = getCanTransferCrypto(account, isCryptoSupported);

  // whether the account is ready to *fully* edit their profile
  const hasOnboarded = [
    "kyc_step",
    "finished",
    "employment_step",
    "financial_profile_step",
  ].includes(account?.last_onboarding_step || "");

  const filtered = useMemo(() => {
    return tabs.filter(
      (tab) =>
        (hasOnboarded || !HIDDEN_WHEN_NOT_ONBOARDED.includes(tab.slug)) &&
        (canTransferCrypto || tab.slug !== ProfileTabs.CRYPTO_FEES),
    );
  }, [hasOnboarded, canTransferCrypto]);

  const tabSlug = useMemo(() => {
    const hash = parseHash(location.hash);
    const match = filtered.find((tab) => tab.slug === hash);
    return match?.slug ?? filtered[0]?.slug;
  }, [filtered, location.hash]);

  const isLoading = !account || !details;

  if (isLoading) {
    return <FullScreenSpinner />;
  }

  const tabProps: SettingsTabProps = {
    account,
    details,
    hasOnboarded,
    trusted_contact,
  };

  const handleTabChange = (slug: ProfileTabSlug) => {
    history.push(profileTabPath(slug));
    refetchAccount();
    refetchDetails();
  };

  return (
    <App>
      <Page
        title="Profile Settings"
        className="flex h-screen flex-col gap-4 p-4 pt-0 xs:pl-0 md:p-4 md:pt-0 md:pl-0"
      >
        <div className="border-gray-200 border-b" />

        <div className="flex h-full flex-col gap-16 overflow-hidden min-[990px]:flex-row">
          <SettingsNavigation
            tabs={filtered}
            activeTabSlug={tabSlug || ""}
            onTabChange={handleTabChange}
          />
          <main className="mt-2 min-w-0 flex-1 overflow-y-auto">
            {filtered.map(
              (tab) =>
                tab.slug === tabSlug && (
                  <tab.component key={tab.slug} {...tabProps} />
                ),
            )}
          </main>
        </div>
      </Page>
    </App>
  );
};

export default ProfileSettings;
